import { createFileRoute, Link } from '@tanstack/react-router'
import { LegalPage, LegalSection } from '@/components/layout/legal-page'
import { APP_NAME } from '@/lib/utils'

const LAST_UPDATED = 'May 18, 2026'

export const Route = createFileRoute('/privacy')({
  component: PrivacyPolicyPage,
  head: () => ({
    meta: [
      {
        title: `${APP_NAME} | Privacy Policy`,
      },
    ],
  }),
})

function PrivacyPolicyPage() {
  return (
    <LegalPage
      title='Privacy Policy'
      lastUpdated={LAST_UPDATED}
      description={`This Privacy Policy explains how ${APP_NAME} collects, uses, and shares information when you use our delivery services and platform.`}
    >
      <LegalSection title='Scope' delayClass='delay-100'>
        <p>
          This policy applies to the ${APP_NAME} web app, mobile experiences,
          integrations, and customer support interactions. It does not cover
          third-party services you may access through our platform.
        </p>
      </LegalSection>

      <LegalSection title='Information we collect' delayClass='delay-150'>
        <ul className='list-disc space-y-2 pl-5'>
          <li>
            Account and profile data such as name, email, phone number, company
            name, and role.
          </li>
          <li>
            Delivery and shipment data such as sender and recipient details,
            addresses, package notes, and proof of delivery.
          </li>
          <li>
            Usage and device data such as IP address, browser type, device
            identifiers, logs, and cookie data.
          </li>
          <li>
            Communications such as support requests, feedback, and operational
            messages.
          </li>
        </ul>
      </LegalSection>

      <LegalSection title='How we use information' delayClass='delay-200'>
        <ul className='list-disc space-y-2 pl-5'>
          <li>Operate, maintain, and improve ${APP_NAME} services.</li>
          <li>Coordinate pickups, deliveries, and status updates.</li>
          <li>Provide support, training, and account management.</li>
          <li>Monitor security, prevent abuse, and enforce policies.</li>
          <li>Analyze usage trends to improve reliability and performance.</li>
        </ul>
      </LegalSection>

      <LegalSection title='Sharing and disclosure' delayClass='delay-250'>
        <ul className='list-disc space-y-2 pl-5'>
          <li>
            Service providers who help with hosting, analytics, messaging, or
            customer support.
          </li>
          <li>
            Delivery partners and clients when needed to complete a shipment or
            provide tracking.
          </li>
          <li>
            Legal or regulatory authorities when required to comply with the
            law.
          </li>
          <li>
            Successor entities in the event of a merger, acquisition, or asset
            sale.
          </li>
        </ul>
      </LegalSection>

      <LegalSection title='Data retention and security' delayClass='delay-300'>
        <p>
          We keep information for as long as needed to provide the services,
          comply with legal obligations, and resolve disputes. We use reasonable
          safeguards designed to protect data, but no system can be completely
          secure.
        </p>
      </LegalSection>

      <LegalSection title='Your choices' delayClass='delay-350'>
        <ul className='list-disc space-y-2 pl-5'>
          <li>Review and update your account details in the platform.</li>
          <li>Manage notification preferences in your profile settings.</li>
          <li>Disable cookies in your browser where applicable.</li>
        </ul>
      </LegalSection>

      <LegalSection title='Contact' delayClass='delay-400'>
        <p>
          If you have questions about this policy, contact your account manager
          or the support team through the ${APP_NAME} platform.
        </p>
      </LegalSection>

      <div className='text-sm text-muted-foreground animate-in fade-in-0 slide-in-from-bottom-4 delay-500 duration-700'>
        Related: <Link to='/terms' className='text-primary underline'>Terms of Service</Link>
      </div>
    </LegalPage>
  )
}
